import _defineProperty from "@babel/runtime/helpers/defineProperty";
// Type declarations are used at compile-time as 'headers' only (does NOT include the files)

import { TaskCanceled } from "abweb/service/rpc/TaskCanceled";
import { ClassUtils } from "abweb/utils/ClassUtils";
import { ErrorExtra, ErrorUtils } from "abweb/utils/ErrorUtils";
import { LogUtils } from "abweb/utils/log/LogUtils";
export { ManualPromise } from "abweb/utils/ManualPromise";
export class PromiseUtils {
  constructor() {
    // static class, can't create an instance
  }

  /**
   * Construct a promise that resolves a specific value after a specific amount of time.
   * Example use is a "stub" for service apis that aren't implemented yet.
   * @param value
   * @param timeoutMsec
   */
  static promiseLater(value, timeoutMsec) {
    return new Promise(resolve => setTimeout(() => resolve(value), timeoutMsec));
  }

  /**
   * Construct a promise that will reject a specific value after a specific amount of time.
   * Example use to for making sure error handling workflows are functioning properly.
   * @param rejectValue
   * @param timeoutMsec
   */
  static rejectLater(rejectValue, timeoutMsec) {
    return new Promise((resolve, reject) => setTimeout(() => reject(rejectValue), timeoutMsec));
  }

  /**
   * Create promise that will sleep for sleepMSec before invoking 'then' block
   *
   * Please note: tests should use TestUtils.waitForInterval instead.
   *
   * @param sleepMSec
   * @return {Promise<void>|Promise}
   */
  static sleep(sleepMSec) {
    return new Promise(function (resolve) {
      if (sleepMSec != null && sleepMSec > 0) {
        // resolve after sleepMSec
        setTimeout(function () {
          resolve();
        }, sleepMSec);
      } else {
        // Immediately resolve
        resolve();
      }
    });
  }

  /**
   * Call setTimeout with a timeout of 0, and then resolve.
   * What's the point?  This may seem like a no-op that would return instantly,
   * but since it starts a new stack frame, it's a useful tool for diagnosing race conditions.
   */
  static sleepZero() {
    return new Promise(function (resolve) {
      // resolve but only after a setTimeout has completed.
      setTimeout(function () {
        resolve();
      }, 0);
    });
  }

  /**
   * Execute iterable (typically an array) of promise functions in sequential order.  Effectively a sequential variation of
   * the built-in Promise.allSettled (including function signature), which invokes the promise functions in parallel.
   *
   * @param promiseFns (Iterable): iterable of promise-returning functions that when invoked, start the async processing.
   * @param stopOnFirstError (boolean): Defaults to false, so that all promiseFns will be
   * evaluated in order regardless of whether errors (aka rejected) are thrown by the individual promiseFn instances.
   * If true, will return immediately after first error, with the last filled in result of the return
   * value containing the failed promise function result (reason).
   *
   * @return tuple, where first item is the 'allSucceeded' boolean indicating whether all promise functions succeeded
   * (no error thrown).
   *  The second tuple item 'results' is an array of PromiseSettledResult result for each invoked promiseFn, indicating
   *  whether it succeeded (PromiseFulfilledResult) or failed (PromiseRejectedResult).
   *  If allSucceeded is false and the stopOnFirstError param was true, then the length of the PromiseSettledResult
   *  array may be less than the number of promiseFns and the last element will contain a PromiseRejectedResult.
   *
   * @see PromiseUtils.forEach for variant that throws on first sequential promiseFn failure.
   **/
  static async sequentialSettled(promiseFns, stopOnFirstError) {
    // static async sequentialSettled<RetV>(promiseFns: Iterable<()=>Promise<RetV>>, stopOnFirstError?: boolean)
    //     : Promise<[allSucceeded: boolean, results: PromiseSettledResult<RetV>[]]> {
    const results = [];
    let allSucceeded = true;
    for (const promiseFn of promiseFns) {
      try {
        const value = await promiseFn();
        results.push({
          status: "fulfilled",
          value
        }); // PromiseFulfilledResult<RetV>
      } catch (reason) {
        results.push({
          status: "rejected",
          reason
        }); // PromiseRejectedResult<RetV>
        allSucceeded = false;
        if (stopOnFirstError) break;
      }
    }
    return [allSucceeded, results];
  }

  /**
   * Execute array of promise functions in sequential order.
   * @return array of successfully resolved results (no error thrown by any promiseFn).
   * @throws exception Exception thrown from first failing promise function.
   * Any thrown exception of an individual promiseFn will stop the sequential processing of the promiseFns.
   * The caller can optionally retrieve the index of the failing promise function from the caught error
   * by using the ErrorUtils.getErrorExtra(error, ErrorExtra.promiseUtilsForEachIndex) helper function.
   *
   * @see PromiseUtils.sequentialSettled for finer grained control of sequential promise function processing.
   */
  static async forEach(promiseFns) {
    const [allSucceeded, settledResults] = await PromiseUtils.sequentialSettled(promiseFns, true);
    if (allSucceeded) {
      return settledResults.map(settledResult => settledResult.value);
    }
    const lastIndex = settledResults.length - 1;
    const err = settledResults[lastIndex].reason;
    ErrorUtils.setErrorExtra(err, ErrorExtra.promiseUtilsForEachIndex, lastIndex.toString());
    throw err;
  }

  /**
   * For those hacky situations were polling is the only option, and you'd still like
   * to have clean code that uses await.
   */
  static resolveByPolling(predicate, pollMsec, abortMsec) {
    const start = new Date().getTime();
    return new Promise((resolve, reject) => {
      const waitSomeMore = () => {
        if (predicate()) {
          resolve();
        } else if (abortMsec != null && new Date().getTime() - start > abortMsec) {
          reject();
        } else {
          setTimeout(waitSomeMore, pollMsec);
        }
      };
      setTimeout(waitSomeMore, 0); // call immediately, and also pass to timeout
    });
  }

  /**
   * Repeatedly call a function that returns a promise. If it rejects, try again in pollMsec millis. Give up trying after abortMsec millis.
   * @param fn function to call repeatedly
   * @param pollDescription logging message to use when trying again
   * @param pollMsec how often to repeat calls
   * @param abortMsec how long to wait before giving up.  If not provided, will poll forever until success
   * @returns resolved value from fn if it resolves, otherwise last rejected value from fn in last poll before timeout
   * @private
   */
  static poll(fn, pollDescription, pollMsec = 1000, abortMsec) {
    const start = new Date().getTime();
    return new Promise((resolve, reject) => {
      const waitSomeMore = async () => {
        try {
          resolve(await fn());
          return; // technically not needed, but makes code more clear
        } catch (error) {
          if (abortMsec != null && new Date().getTime() - start > abortMsec) {
            PromiseUtils.LOG.warn(`Abort polling after ${abortMsec} msec for poll: '${pollDescription}'`);
            reject(error);
            return;
          }
          PromiseUtils.LOG.info(`poll failed, re-polling: '${pollDescription}'`);
          setTimeout(waitSomeMore, pollMsec);
        }
      };
      setTimeout(waitSomeMore, 0);
    });
  }

  /**
   * Generic method which logs a reason for rejecting a promise and returns.  Better than nothing.
   * Always throws AlreadyHandledError (which will eventually fall off the end of promise chain).
   * If incoming reason is 'AlreadyHandlerError' just rethrows, else it logs it and wraps in a new AlreadyHandledError
   * and throws.
   * @param reason
   * @throws AlreadyHandledError
   *
   */
  static throwAlreadyHandled(reason) {
    if (ClassUtils.isInstanceOf(reason, AlreadyHandledError)) {
      throw reason;
    }
    let msg = "" + reason;
    if (reason.hasOwnProperty("stack")) {
      msg += "\n" + reason.stack;
    }
    PromiseUtils.LOG.error("Promise rejected: " + msg);
    throw new AlreadyHandledError(reason);
  }

  /**
   * If an error is an AlreadyHandledError type, rethrow it, otherwise, do nothing
   * @param err - the error in question
   */
  static rethrowIfAlreadyHandledError(err) {
    if (ClassUtils.isInstanceOf(err, AlreadyHandledError)) {
      throw err;
    }
  }

  /**
   * When working with an AsyncTask, you may need to catch a TaskCanceled
   * in order to prevent it from bubbling up to a last chance error handler.
   * This utility function allows that to be done with one line,
   * while still allowing other reject exceptions to continue bubbling.
   *
   * Naturally, this returns a Promise<void> since there may be no value to return.
   */
  static async swallowCancelException(prom) {
    // console.log(":swallowCancelException"); //`
    try {
      await prom;
    } catch (reason) {
      if (ClassUtils.isInstanceOf(reason, TaskCanceled)) {
        // swallow, the TaskCanceled, as intended
        // console.log(`swallowCancelException`); //`
      } else {
        // rethrow all other errors.
        throw reason;
      }
    }
  }

  /**
   * When working with an AsyncTask, you may need to catch a TaskCanceled
   * in order to prevent it from bubbling up to a last chance error handler.
   * This utility function allows that to be done with one line,
   * while still allowing other reject exceptions to continue bubbling.
   *
   * Naturally, this returns a Promise<void> since there may be no value to return.
   */
  static async swallowCancelException2(prom) {
    try {
      const ret = await prom;
      return ret;
    } catch (reason) {
      if (ClassUtils.isInstanceOf(reason, TaskCanceled)) {
        // swallow, the TaskCanceled, as intended
        // console.log(`swallowCancelException`); //`
        return undefined;
      } else {
        // rethrow all other errors.
        throw reason;
      }
    }
  }

  /**
   * When working with an AsyncTask, you may need to handle the cancel scenario.
   * Since it can be awkward and even error-prone to handle that with a catch,
   * this utility will catch and return the TaskCanceled object to make a different
   * code construction possible.
   *
   * e.g. const ret: string | TaskCanceled = await PromiseUtils.cancelAsReturnValue(prom);
   */
  static async cancelAsReturnValue(prom) {
    // console.log(":swallowCancelException"); //`
    try {
      const ret = await prom;
      return ret;
    } catch (reason) {
      if (ClassUtils.isInstanceOf(reason, TaskCanceled)) {
        // return the TaskCanceled object
        return reason;
      } else {
        // rethrow all other errors.
        throw reason;
      }
    }
  }

  /**
   * When holding a copy of a promise so that you can wait for it to complete,
   * any errors thrown or rejected will be duplicated by an await on the copy.
   * To prevent those errors from bubbling to the last chance handler, intentionally catch and swallow them.
   *
   * Naturally, this returns a Promise<void> since there may be no value to return.
   */
  static async awaitSwallowingRejects(prom) {
    // console.log(`swallowErrors2`); //`

    try {
      await prom;
    } catch (reason) {
      // swallow the error, as intended
    }
  }
}

/**
 * construction that simplifies the cognitive load when creating a promise
 * and have the resolved value be provided by a callback far away.
 *
 * e.g. code like this:
 * this.deferredValue = new DeferredValuePromise<number>;
 * <button onClick={()=>this.deferredValue.resolveIt(3.14)} />
 * value = await.deferredValue.awaitValue();
 *
 */
_defineProperty(PromiseUtils, "LOG", LogUtils.getLogger(__filename));
export class DeferredValuePromise {
  constructor() {
    _defineProperty(this, "resultPromise", void 0);
    // save a promise off so it can be awaited for
    this.resultPromise = new Promise((resolve, reject) => {
      // console.log(`inside resultPromise`); //`
      this.resolve = resolve;
      this.reject = reject;
    });
  }
  resolveIt(value) {
    // console.log(`resolveIt`); //`
    this.resolve(value);
  }
  rejectIt(reason) {
    this.reject(reason);
  }

  /**
   * allows you to write :
   * choice = await question.untilChoiceMade();
   */
  async awaitValue() {
    // console.log(`awaitValue start`); //`
    return this.resultPromise;
    // console.log(`awaitValue done`); //`
  }
}

/**
 * A promise wrapper that supports being canceled.
 *
 * A canceled promise will invoke reject with a CanceledPromiseError.
 *
 * Note: this implementation is based upon a recommendation from the React blog:
 * https://reactjs.org/blog/2015/12/16/ismounted-antipattern.html
 */
export class CancelablePromise {
  constructor(promise) {
    _defineProperty(this, "promise", void 0);
    _defineProperty(this, "_canceled", false);
    this.promise = new Promise(function (resolve, reject) {
      promise.then(value => this.canceled ? reject && reject(new PromiseCanceled()) : resolve && resolve(value), reason => reject && reject(this.canceled ? new PromiseCanceled() : reason));
    }.bind(this));
  }
  get canceled() {
    return this._canceled;
  }
  cancel() {
    this._canceled = true;
  }
}

/**
 * The class of error received by a promise's reject method when it has been canceled.
 */
export class PromiseCanceled {}
export class AlreadyHandledError extends Error {
  /**
   * Wrapper class used for halting a Promise execution.
   * @param underlyingError - error that we want to wrap as an "already handled" error
   */
  constructor(underlyingError) {
    super();
    this.underlyingError = underlyingError;
    this.message = AlreadyHandledError.alreadyHandled + underlyingError.message;
  }
}

/**
 * Utility for chaining promises one after another.
 */
_defineProperty(AlreadyHandledError, "alreadyHandled", "AlreadyHandledError: ");
export class PromiseChain {
  constructor() {
    _defineProperty(this, "promiseChain", Promise.resolve());
  }
  execute(fn) {
    const ret = this.promiseChain.then(fn);
    // Handle both normal returns and errors for the chain.
    this.promiseChain = ret.then(() => {}, () => {});
    return ret;
  }
}
//# sourceMappingURL=PromiseUtils.js.map
